import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import AnnouncementBar from '../components/AnnouncementBar';
import { CheckCircle2, Loader2, XCircle, ArrowRight, Zap, ShieldCheck } from 'lucide-react';

const MAX_POLLS = 30;    // ~60s total
const POLL_INTERVAL = 2000;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();

  const [status, setStatus] = useState('polling'); // polling | paid | timeout | error
  const [data, setData] = useState(null);
  const pollsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    const backend = process.env.REACT_APP_BACKEND_URL;

    const poll = async () => {
      try {
        const res = await fetch(`${backend}/api/payments/status/${sessionId}`);
        if (res.status === 404) {
          setStatus('error');
          return;
        }
        if (!res.ok) throw new Error('status fetch failed');
        const d = await res.json();
        setData(d);
        if (d.payment_status === 'paid') {
          setStatus('paid');
          return;
        }
        if (d.payment_status === 'failed' || d.payment_status === 'expired') {
          setStatus('error');
          return;
        }
      } catch {
        // silent — will retry
      }
      pollsRef.current += 1;
      if (pollsRef.current >= MAX_POLLS) {
        setStatus('timeout');
        return;
      }
      timerRef.current = setTimeout(poll, POLL_INTERVAL);
    };

    poll();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [sessionId]);

  return (
    <div className="min-h-screen overflow-x-hidden">
      <AnnouncementBar />
      <Header />

      <div className="max-w-lg mx-auto px-4 sm:px-6 pt-10 pb-16 text-center">
        {status === 'polling' && (
          <div className="glass-card rounded-3xl p-8" data-testid="payment-polling">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-white/5 flex items-center justify-center mb-4">
              <Loader2 className="w-8 h-8 text-[#ff8247] animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Validation du paiement…
            </h1>
            <p className="text-white/60 text-sm">Merci de patienter quelques secondes.</p>
          </div>
        )}

        {status === 'paid' && (
          <div className="glass-card rounded-3xl p-8" data-testid="payment-success">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#00b67a]/15 border border-[#00b67a]/30 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-9 h-9 text-[#00b67a]" />
            </div>
            <div className="text-[#00b67a] text-[10px] font-bold uppercase tracking-widest mb-2">Paiement confirmé</div>
            <h1 className="text-3xl font-bold text-white leading-tight mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Votre <span className="gradient-text">boost</span> est lancé
            </h1>
            <p className="text-white/70 text-sm mb-6">
              Nous avons envoyé votre commande au fournisseur. La livraison démarre dans les 12 h prochaines heures, de manière progressive.
            </p>

            {data && (
              <div className="glass-card rounded-2xl p-4 mb-6 text-left">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-white/60">Service</span>
                  <span className="text-white font-semibold text-right truncate max-w-[60%]">{data.product_name || 'Boost'}</span>
                </div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-white/60">Quantité</span>
                  <span className="text-white font-semibold">{Number(data.qty || 0).toLocaleString('fr-FR')}</span>
                </div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-white/60">Lien cible</span>
                  <span className="text-white/80 text-xs truncate max-w-[60%]">{data.handle}</span>
                </div>
                <div className="h-px bg-white/10 my-3" />
                <div className="flex items-center justify-between">
                  <span className="text-white/80 text-sm">Montant payé</span>
                  <span className="text-white font-bold text-xl" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {Number(data.amount_eur || 0).toFixed(2)} €
                  </span>
                </div>
                {data.smm_order_id && (
                  <div className="mt-3 text-white/50 text-xs">N° de commande : <span className="text-white font-semibold">#{data.smm_order_id}</span></div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 mb-5">
              <div className="glass-card rounded-xl p-3 flex flex-col items-center gap-1">
                <Zap className="w-4 h-4 text-[#ff8247]" />
                <div className="text-white text-[11px] font-semibold">Livraison 12h</div>
              </div>
              <div className="glass-card rounded-xl p-3 flex flex-col items-center gap-1">
                <ShieldCheck className="w-4 h-4 text-[#ff8247]" />
                <div className="text-white text-[11px] font-semibold">Garantie renvoi</div>
              </div>
            </div>

            <button onClick={() => navigate('/account')} data-testid="payment-success-account-btn"
              className="btn-primary w-full justify-center inline-flex items-center gap-2 text-sm">
              Voir mes commandes <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {status === 'timeout' && (
          <div className="glass-card rounded-3xl p-8" data-testid="payment-timeout">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-white/5 flex items-center justify-center mb-4">
              <Loader2 className="w-8 h-8 text-white/40" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">La confirmation prend plus de temps que d’habitude</h1>
            <p className="text-white/60 text-sm mb-6">
              Votre paiement est en cours de traitement. Vous recevrez la confirmation par email dès qu'il sera validé.
            </p>
            <Link to="/account" className="btn-primary inline-flex items-center gap-2 text-sm">
              Voir mes commandes <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="glass-card rounded-3xl p-8" data-testid="payment-error">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-4">
              <XCircle className="w-9 h-9 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Paiement non confirmé</h1>
            <p className="text-white/60 text-sm mb-6">
              Nous n'avons pas pu confirmer votre paiement. Aucune somme n'a été prélevée si le paiement a échoué.
            </p>
            <Link to="/collections" className="btn-primary inline-flex items-center gap-2 text-sm">
              Retour au catalogue <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
